import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import {
  ManagedEntitiesOrganizationsQuery,
  ManagedEntitiesPropertiesQuery,
  ManagedEntitiesCoresQuery,
  ManagedEntitiesHmMsQuery,
  ManagedEntitiesBnGsQuery,
  ManagedEntitiesConductorsQuery,
  ManagedEntitiesBuildingsQuery,
  ManagedEntitiesBuildingsByPropertyIdQuery,
  ManagedEntitiesFloorsQuery,
  ManagedEntitiesFloorsByPropertyIdQuery,
  ManagedEntitiesUnitsByPropertyIdsQuery,
  ManagedEntitiesUnitsQuery,
  ManagedEntitiesPlmEventResponsesByPropertyIdsQuery,
  ManagedEntitiesPlmEventResponsesQuery,
  ManagedEntitiesThermostatsQuery,
  ManagedEntitiesThermostatsByPropertyIdQuery,
  useManagedEntitiesOrganizationsQuery,
  useManagedEntitiesPropertiesQuery,
  useManagedEntitiesCoresQuery,
  useManagedEntitiesHmMsQuery,
  useManagedEntitiesBnGsQuery,
  useManagedEntitiesConductorsQuery,
  useManagedEntitiesBuildingsQuery,
  useManagedEntitiesBuildingsByPropertyIdQuery,
  useManagedEntitiesFloorsQuery,
  useManagedEntitiesFloorsByPropertyIdQuery,
  useManagedEntitiesUnitsByPropertyIdsQuery,
  useManagedEntitiesUnitsQuery,
  useManagedEntitiesPlmEventResponsesByPropertyIdsQuery,
  useManagedEntitiesPlmEventResponsesQuery,
  useManagedEntitiesThermostatsByPropertyIdQuery,
  useManagedOrganizationUpdatesSubscription,
  useManagedPropertyUpdatesSubscription,
  useManagedUnitUpdatesSubscription,
  ManagedPropertyUpdatesSubscription,
  ManagedUnitUpdatesByPropertyIdsSubscription,
  ManagedUnitUpdatesSubscription,
  ManagedBuildingUpdatesSubscription,
  ManagedFloorUpdatesSubscription,
  useManagedBuildingUpdatesSubscription,
  useManagedFloorUpdatesSubscription,
  useManagedCoreUpdatesSubscription,
  useManagedHmmUpdatesSubscription,
  useManagedBngUpdatesSubscription,
  useManagedConductorUpdatesSubscription,
  ManagedCoreUpdatesSubscription,
  ManagedHmmUpdatesSubscription,
  ManagedBngUpdatesSubscription,
  ManagedConductorUpdatesSubscription,
  ManagedOrganizationUpdatesSubscription,
  useManagedUnitUpdatesByPropertyIdsSubscription,
  useManagedPlmEventResponseUpdatesSubscription,
  ManagedPlmEventResponseUpdatesSubscription,
} from '../../types/generated-types';

import { Log, LogCategory } from './services/logger';

import { OnDataOptions } from '@apollo/client/react/types/types';
import { singularize } from 'inflected';
import { ApolloError } from '@apollo/client';
import { useAuthenticator } from '../auth/AuthenticationContext';
import { useAuthorizer } from '../auth/AuthorizationContext';
import { useSystemConnection } from './ConnectionManager';

export type ManagedOrganization = NonNullable<
  NonNullable<ManagedEntitiesOrganizationsQuery>
>['organizations'][number];

export type UpdatedManagedOrganization = NonNullable<
  NonNullable<ManagedOrganizationUpdatesSubscription>
>['organizationEvents'];

export type ManagedProperty = NonNullable<
  NonNullable<ManagedEntitiesPropertiesQuery>
>['properties'][number];

export type UpdatedManagedProperty = NonNullable<
  NonNullable<ManagedPropertyUpdatesSubscription>
>['propertyEvents'];

export type ManagedHmm = NonNullable<
  NonNullable<ManagedEntitiesHmMsQuery>
>['hMMs'][number];

export type UpdatedManagedHmm = NonNullable<
  NonNullable<ManagedHmmUpdatesSubscription>
>['hMMEvents'];

export type ManagedCore = NonNullable<
  NonNullable<ManagedEntitiesCoresQuery>
>['cores'][number];

export type UpdatedManagedCore = NonNullable<
  NonNullable<ManagedCoreUpdatesSubscription>
>['coreEvents'];

export type ManagedBng = NonNullable<
  NonNullable<ManagedEntitiesBnGsQuery>
>['bNGs'][number];

export type UpdatedManagedBng = NonNullable<
  NonNullable<ManagedBngUpdatesSubscription>
>['bNGEvents'];

export type ManagedConductor = NonNullable<
  NonNullable<ManagedEntitiesConductorsQuery>
>['conductors'][number];

export type UpdatedManagedConductor = NonNullable<
  NonNullable<ManagedConductorUpdatesSubscription>
>['conductorEvents'];

export type ManagedSource =
  | ManagedHmm
  | ManagedCore
  | ManagedBng
  | ManagedConductor;
export type UpdatedManagedSource =
  | UpdatedManagedHmm
  | UpdatedManagedCore
  | UpdatedManagedBng
  | UpdatedManagedConductor;

export type ManagedBuilding = NonNullable<
  NonNullable<ManagedEntitiesBuildingsQuery>
>['buildings'][number];

export type ManagedBuildingByPropertyId = NonNullable<
  NonNullable<ManagedEntitiesBuildingsByPropertyIdQuery>
>['buildings'][number];

export type UpdatedManagedBuilding = NonNullable<
  NonNullable<ManagedBuildingUpdatesSubscription>
>['buildingEvents'];

export type ManagedFloor = NonNullable<
  NonNullable<ManagedEntitiesFloorsQuery>
>['floors'][number];

export type ManagedFloorByPropertyId = NonNullable<
  NonNullable<ManagedEntitiesFloorsByPropertyIdQuery>
>['floors'][number];

export type UpdatedManagedFloor = NonNullable<
  NonNullable<ManagedFloorUpdatesSubscription>
>['floorEvents'];

export type ManagedUnit = NonNullable<
  NonNullable<ManagedEntitiesUnitsQuery>
>['units'][number];

export type ManagedUnitByPropertyId = NonNullable<
  NonNullable<ManagedEntitiesUnitsByPropertyIdsQuery>
>['unitsByPropertyId'][number];

export type UpdatedManagedUnit = NonNullable<
  NonNullable<ManagedUnitUpdatesSubscription>
>['unitEvents'];

export type ManagedPlmEventResponse = NonNullable<
  NonNullable<ManagedEntitiesPlmEventResponsesQuery>
>['plmEventResponses'][number];

export type ManagedPlmEventResponseByPropertyId = NonNullable<
  NonNullable<ManagedEntitiesPlmEventResponsesByPropertyIdsQuery>
>['plmEventResponses'][number];

export type UpdatedManagedPlmEventResponse = NonNullable<
  NonNullable<ManagedPlmEventResponseUpdatesSubscription>
>['plmEventResponseEvents'];

export type ManagedOrganizationsMap = Record<string, ManagedOrganization>;

export type ManagedPropertiesMap = Record<string, ManagedProperty>;

export type ManagedCoresMap = Record<string, ManagedCore>;

export type ManagedHmmsMap = Record<string, ManagedHmm>;

export type ManagedBngsMap = Record<string, ManagedBng>;
export type ManagedConductorsMap = Record<string, ManagedConductor>;

export type ManagedSourcesMap = Record<string, ManagedSource>;

export type ManagedBuildingsMap = Record<string, ManagedBuilding>;

export type ManagedFloorsMap = Record<string, ManagedFloor>;

export type ManagedUnitsMap = Record<string, ManagedUnit>;

export type ManagedPlmEventResponsesMap = Record<
  string,
  ManagedPlmEventResponse
>;

export type EntityManagerThermostat =
  | ManagedEntitiesThermostatsQuery['thermostats'][number]
  | ManagedEntitiesThermostatsByPropertyIdQuery['thermostats'][number];

type UpdatedEntity =
  | UpdatedManagedOrganization
  | UpdatedManagedProperty
  | UpdatedManagedSource
  | UpdatedManagedBuilding
  | UpdatedManagedFloor
  | UpdatedManagedUnit
  | UpdatedManagedPlmEventResponse;

export interface IEntityManagerData {
  organizations: ManagedOrganizationsMap;
  properties: ManagedPropertiesMap;
  cores: ManagedCoresMap;
  hmms: ManagedHmmsMap;
  bngs: ManagedBngsMap;
  conductors: ManagedConductorsMap;
  sources: ManagedSourcesMap;
  buildings: ManagedBuildingsMap;
  floors: ManagedFloorsMap;
  units: ManagedUnitsMap;
  plmEventResponses: ManagedPlmEventResponsesMap;
}

export interface IEntityManagerLoadingStates {
  organizations: boolean;
  properties: boolean;
  cores: boolean;
  hmms: boolean;
  bngs: boolean;
  conductors: boolean;
  buildings: boolean;
  floors: boolean;
  units: boolean;
  plmEventResponses: boolean;
}

export interface IEntityManagerErrors {
  organizations?: ApolloError;
  properties?: ApolloError;
  cores?: ApolloError;
  hmms?: ApolloError;
  bngs?: ApolloError;
  conductors?: ApolloError;
  buildings?: ApolloError;
  floors?: ApolloError;
  units?: ApolloError;
  plmEventResponses?: ApolloError;
}

export type ManagedEntityDataKey = keyof IEntityManagerLoadingStates;
type ManagedEntityDemandState = Record<ManagedEntityDataKey, boolean>;

export interface IEntityManager extends IEntityManagerData {
  loading: boolean; // Global loading state (true if any entity type is loading)
  loadingStates: IEntityManagerLoadingStates; // Granular loading states per entity type
  error?: ApolloError; // Global error state (first error encountered)
  errors: IEntityManagerErrors; // Granular error states per entity type
  organizations: ManagedOrganizationsMap;
  properties: ManagedPropertiesMap;
  cores: ManagedCoresMap;
  hmms: ManagedHmmsMap;
  bngs: ManagedBngsMap;
  conductors: ManagedConductorsMap;
  sources: ManagedSourcesMap;
  buildings: ManagedBuildingsMap;
  floors: ManagedFloorsMap;
  units: ManagedUnitsMap;
  plmEventResponses: ManagedPlmEventResponsesMap;
  requestedEntityData: ManagedEntityDemandState;
  requestEntityData: (entityKeys: ManagedEntityDataKey[]) => void;
}

const defaultEntityMap: IEntityManagerData = {
  organizations: {},
  properties: {},
  cores: {},
  hmms: {},
  bngs: {},
  conductors: {},
  sources: {},
  buildings: {},
  floors: {},
  units: {},
  plmEventResponses: {},
};

const defaultLoadingStates: IEntityManagerLoadingStates = {
  organizations: false,
  properties: false,
  cores: false,
  hmms: false,
  bngs: false,
  conductors: false,
  buildings: false,
  floors: false,
  units: false,
  plmEventResponses: false,
};

const defaultErrors: IEntityManagerErrors = {};

const defaultEntityDemandState: ManagedEntityDemandState = {
  organizations: true,
  properties: true,
  cores: false,
  hmms: false,
  bngs: false,
  conductors: false,
  buildings: false,
  floors: false,
  units: false,
  plmEventResponses: false,
};

const defaultValue: IEntityManager = {
  ...defaultEntityMap,
  loading: false,
  loadingStates: defaultLoadingStates,
  errors: defaultErrors,
  requestedEntityData: defaultEntityDemandState,
  requestEntityData: () => {
    // default - no-op
  },
};

interface ManagedEntityDemandContextValue {
  requestedEntityData: ManagedEntityDemandState;
  requestedUnitPropertyIds: string[];
  requestedPlmEventResponsePropertyIds: string[];
  requestEntityData: (entityKeys: ManagedEntityDataKey[]) => void;
  requestUnitPropertyData: (propertyId?: string | null | undefined) => void;
  requestPlmEventResponsePropertyData: (
    propertyId?: string | null | undefined,
  ) => void;
}

export interface IHookUnitsByPropertyId {
  units: ManagedUnit[];
  loading: boolean;
  error?: ApolloError;
}

export interface IHookUnitById {
  unit: ManagedUnit | null;
  loading: boolean;
  error?: ApolloError;
}

export interface IHookProperties {
  properties: ManagedProperty[];
  loading: boolean;
  error?: ApolloError;
}

export interface IHookPropertyById {
  property: ManagedProperty | null;
  loading: boolean;
  error?: ApolloError;
}

interface ManagedUnitStoreStatus {
  loading: boolean;
  error?: ApolloError;
}

interface ManagedPropertyStoreStatus {
  loading: boolean;
  error?: ApolloError;
}

interface ManagedPropertyStore {
  getPropertiesSnapshot: () => IHookProperties;
  getPropertySnapshot: (id?: string | null) => IHookPropertyById;
  replaceProperties: (
    nextProperties: ManagedPropertiesMap,
    status: ManagedPropertyStoreStatus,
  ) => void;
  subscribeProperties: (listener: () => void) => () => void;
  subscribeProperty: (id: string, listener: () => void) => () => void;
}

interface ManagedUnitStore {
  getUnitSnapshot: (id?: string | null) => IHookUnitById;
  getUnitsByPropertySnapshot: (propertyId: string) => IHookUnitsByPropertyId;
  replaceUnits: (
    nextUnits: ManagedUnitsMap,
    status: ManagedUnitStoreStatus,
    propertyStatuses?: Record<string, ManagedUnitStoreStatus>,
  ) => void;
  subscribeUnit: (id: string, listener: () => void) => () => void;
  subscribeUnitsByProperty: (
    propertyId: string,
    listener: () => void,
  ) => () => void;
}

function normalizeManagedUnitStoreKey(
  value?: string | null | undefined,
): string {
  return value?.trim() ?? '';
}

function normalizeManagedPropertyStoreKey(
  value?: string | null | undefined,
): string {
  return value?.trim() ?? '';
}

function normalizeManagedPlmEventResponseStoreKey(
  value?: string | null | undefined,
): string {
  return value?.trim() ?? '';
}

function subscribeToListenerMap(
  listenersByKey: Map<string, Set<() => void>>,
  key: string,
  listener: () => void,
): () => void {
  const listeners = listenersByKey.get(key) ?? new Set();
  listeners.add(listener);
  listenersByKey.set(key, listeners);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      listenersByKey.delete(key);
    }
  };
}

function notifyListenersForKey(
  listenersByKey: Map<string, Set<() => void>>,
  key: string,
) {
  listenersByKey.get(key)?.forEach((listener) => listener());
}

function notifyAllListeners(listenersByKey: Map<string, Set<() => void>>) {
  listenersByKey.forEach((listeners) => {
    listeners.forEach((listener) => listener());
  });
}

export function createManagedPropertyStore(): ManagedPropertyStore {
  let properties: ManagedPropertiesMap = {};
  let loading = false;
  let error: ApolloError | undefined;
  let propertiesSnapshot: IHookProperties | null = null;
  const propertyListenersById = new Map<string, Set<() => void>>();
  const propertiesListeners = new Set<() => void>();
  const propertySnapshotsById = new Map<string, IHookPropertyById>();

  function getPropertiesSnapshot(): IHookProperties {
    if (propertiesSnapshot) {
      return propertiesSnapshot;
    }

    propertiesSnapshot = {
      properties: Object.values(properties),
      loading,
      error,
    };
    return propertiesSnapshot;
  }

  function getPropertySnapshot(id?: string | null): IHookPropertyById {
    const normalizedId = normalizeManagedPropertyStoreKey(id);
    const cachedSnapshot = propertySnapshotsById.get(normalizedId);
    if (cachedSnapshot) {
      return cachedSnapshot;
    }

    const snapshot = {
      property: normalizedId ? (properties[normalizedId] ?? null) : null,
      loading,
      error,
    };
    propertySnapshotsById.set(normalizedId, snapshot);
    return snapshot;
  }

  function notifyPropertiesListeners() {
    propertiesListeners.forEach((listener) => listener());
  }

  function replaceProperties(
    nextProperties: ManagedPropertiesMap,
    status: ManagedPropertyStoreStatus,
  ) {
    const statusChanged = loading !== status.loading || error !== status.error;
    const changedPropertyIds = new Set<string>();
    const allPropertyIds = new Set([
      ...Object.keys(properties),
      ...Object.keys(nextProperties),
    ]);

    allPropertyIds.forEach((propertyId) => {
      if (properties[propertyId] !== nextProperties[propertyId]) {
        changedPropertyIds.add(propertyId);
      }
    });

    properties = nextProperties;
    loading = status.loading;
    error = status.error;

    if (statusChanged) {
      propertiesSnapshot = null;
      propertySnapshotsById.clear();
      notifyPropertiesListeners();
      notifyAllListeners(propertyListenersById);
      return;
    }

    if (changedPropertyIds.size > 0) {
      propertiesSnapshot = null;
      notifyPropertiesListeners();
    }

    changedPropertyIds.forEach((propertyId) => {
      propertySnapshotsById.delete(propertyId);
      notifyListenersForKey(propertyListenersById, propertyId);
    });
  }

  return {
    getPropertiesSnapshot,
    getPropertySnapshot,
    replaceProperties,
    subscribeProperties(listener: () => void) {
      propertiesListeners.add(listener);

      return () => {
        propertiesListeners.delete(listener);
      };
    },
    subscribeProperty(id: string, listener: () => void) {
      return subscribeToListenerMap(
        propertyListenersById,
        normalizeManagedPropertyStoreKey(id),
        listener,
      );
    },
  };
}

export function createManagedUnitStore(): ManagedUnitStore {
  let units: ManagedUnitsMap = {};
  let loading = false;
  let error: ApolloError | undefined;
  let propertyStatusesById: Record<string, ManagedUnitStoreStatus> = {};
  const unitListenersById = new Map<string, Set<() => void>>();
  const propertyListenersById = new Map<string, Set<() => void>>();
  const unitSnapshotsById = new Map<string, IHookUnitById>();
  const propertySnapshotsById = new Map<string, IHookUnitsByPropertyId>();

  function getUnitSnapshot(id?: string | null): IHookUnitById {
    const normalizedId = normalizeManagedUnitStoreKey(id);
    const cachedSnapshot = unitSnapshotsById.get(normalizedId);
    if (cachedSnapshot) {
      return cachedSnapshot;
    }

    const snapshot = {
      unit: normalizedId ? (units[normalizedId] ?? null) : null,
      loading,
      error,
    };
    unitSnapshotsById.set(normalizedId, snapshot);
    return snapshot;
  }

  function getUnitsByPropertySnapshot(
    propertyId: string,
  ): IHookUnitsByPropertyId {
    const normalizedPropertyId = normalizeManagedUnitStoreKey(propertyId);
    const cachedSnapshot = propertySnapshotsById.get(normalizedPropertyId);
    if (cachedSnapshot) {
      return cachedSnapshot;
    }
    const propertyStatus = propertyStatusesById[normalizedPropertyId];

    const snapshot = {
      units: normalizedPropertyId
        ? Object.values(units).filter(
            (unit) => unit.propertyId === normalizedPropertyId,
          )
        : [],
      loading: loading || propertyStatus?.loading || false,
      error: error || propertyStatus?.error,
    };
    propertySnapshotsById.set(normalizedPropertyId, snapshot);
    return snapshot;
  }

  function replaceUnits(
    nextUnits: ManagedUnitsMap,
    status: ManagedUnitStoreStatus,
    propertyStatuses: Record<string, ManagedUnitStoreStatus> = {},
  ) {
    const statusChanged = loading !== status.loading || error !== status.error;
    const changedUnitIds = new Set<string>();
    const changedPropertyIds = new Set<string>();
    const changedPropertyStatusIds = new Set<string>();
    const allUnitIds = new Set([
      ...Object.keys(units),
      ...Object.keys(nextUnits),
    ]);
    const allPropertyStatusIds = new Set([
      ...Object.keys(propertyStatusesById),
      ...Object.keys(propertyStatuses),
    ]);

    allUnitIds.forEach((unitId) => {
      const previousUnit = units[unitId];
      const nextUnit = nextUnits[unitId];

      if (previousUnit !== nextUnit) {
        changedUnitIds.add(unitId);
        if (previousUnit?.propertyId) {
          changedPropertyIds.add(previousUnit.propertyId);
        }
        if (nextUnit?.propertyId) {
          changedPropertyIds.add(nextUnit.propertyId);
        }
      }
    });
    allPropertyStatusIds.forEach((propertyId) => {
      const previousStatus = propertyStatusesById[propertyId];
      const nextStatus = propertyStatuses[propertyId];

      if (
        previousStatus?.loading !== nextStatus?.loading ||
        previousStatus?.error !== nextStatus?.error
      ) {
        changedPropertyStatusIds.add(propertyId);
      }
    });

    units = nextUnits;
    loading = status.loading;
    error = status.error;
    propertyStatusesById = propertyStatuses;

    if (statusChanged) {
      unitSnapshotsById.clear();
      propertySnapshotsById.clear();
      notifyAllListeners(unitListenersById);
      notifyAllListeners(propertyListenersById);
      return;
    }

    changedPropertyStatusIds.forEach((propertyId) => {
      propertySnapshotsById.delete(propertyId);
      notifyListenersForKey(propertyListenersById, propertyId);
    });
    changedUnitIds.forEach((unitId) => {
      unitSnapshotsById.delete(unitId);
      notifyListenersForKey(unitListenersById, unitId);
    });
    changedPropertyIds.forEach((propertyId) => {
      propertySnapshotsById.delete(propertyId);
      notifyListenersForKey(propertyListenersById, propertyId);
    });
  }

  return {
    getUnitSnapshot,
    getUnitsByPropertySnapshot,
    replaceUnits,
    subscribeUnit(id: string, listener: () => void) {
      return subscribeToListenerMap(
        unitListenersById,
        normalizeManagedUnitStoreKey(id),
        listener,
      );
    },
    subscribeUnitsByProperty(propertyId: string, listener: () => void) {
      return subscribeToListenerMap(
        propertyListenersById,
        normalizeManagedUnitStoreKey(propertyId),
        listener,
      );
    },
  };
}

function createManagedUnitsMap(
  unitList: Array<ManagedUnit | ManagedUnitByPropertyId>,
): ManagedUnitsMap {
  return unitList.reduce((res, entity) => {
    res[entity._id] = entity as ManagedUnit;
    return res;
  }, {} as ManagedUnitsMap);
}

function mergeManagedUnitsForProperties(
  currentUnits: ManagedUnitsMap,
  scopedUnits: ManagedUnitByPropertyId[],
  propertyIds: string[],
): ManagedUnitsMap {
  const scopedPropertyIds = new Set(propertyIds);
  const nextUnits = Object.values(currentUnits).reduce((res, unit) => {
    if (!scopedPropertyIds.has(unit.propertyId)) {
      res[unit._id] = unit;
    }
    return res;
  }, {} as ManagedUnitsMap);

  scopedUnits.forEach((unit) => {
    nextUnits[unit._id] = unit as ManagedUnit;
  });

  return nextUnits;
}

function createManagedPlmEventResponsesMap(
  plmEventResponseList: Array<
    ManagedPlmEventResponse | ManagedPlmEventResponseByPropertyId
  >,
): ManagedPlmEventResponsesMap {
  return plmEventResponseList.reduce((res, entity) => {
    res[entity._id] = entity as ManagedPlmEventResponse;
    return res;
  }, {} as ManagedPlmEventResponsesMap);
}

function mergeManagedPlmEventResponsesForProperties(
  currentPlmEventResponses: ManagedPlmEventResponsesMap,
  scopedPlmEventResponses: ManagedPlmEventResponseByPropertyId[],
  propertyIds: string[],
): ManagedPlmEventResponsesMap {
  const scopedPropertyIds = new Set(propertyIds);
  const nextPlmEventResponses = Object.values(currentPlmEventResponses).reduce(
    (res, plmEventResponse) => {
      if (
        !plmEventResponse.propertyId ||
        !scopedPropertyIds.has(plmEventResponse.propertyId)
      ) {
        res[plmEventResponse._id] = plmEventResponse;
      }
      return res;
    },
    {} as ManagedPlmEventResponsesMap,
  );

  scopedPlmEventResponses.forEach((plmEventResponse) => {
    nextPlmEventResponses[plmEventResponse._id] =
      plmEventResponse as ManagedPlmEventResponse;
  });

  return nextPlmEventResponses;
}

interface ManagedEntitiesProviderProps {
  children: React.ReactNode;
}

const ManagedEntitiesContext = createContext<IEntityManager>(defaultValue);
const ManagedEntityDemandContext = createContext<
  ManagedEntityDemandContextValue | undefined
>(undefined);
const ManagedPropertyStoreContext = createContext<
  ManagedPropertyStore | undefined
>(undefined);
const ManagedUnitStoreContext = createContext<ManagedUnitStore | undefined>(
  undefined,
);

ManagedEntitiesContext.displayName = 'ManagedEntitiesContext';
ManagedEntityDemandContext.displayName = 'ManagedEntityDemandContext';
ManagedPropertyStoreContext.displayName = 'ManagedPropertyStoreContext';
ManagedUnitStoreContext.displayName = 'ManagedUnitStoreContext';

export const ManagedEntitiesProvider: FC<ManagedEntitiesProviderProps> = ({
  children,
}) => {
  const { signingOut } = useAuthenticator();
  const { can } = useAuthorizer();
  const { connectionRefreshGeneration } = useSystemConnection();
  const canAccessPlm = can('be', 'super');
  const [managedEntities, setManagedEntities] =
    useState<IEntityManager>(defaultValue);
  const [requestedEntityData, setRequestedEntityData] =
    useState<ManagedEntityDemandState>(defaultEntityDemandState);
  const [requestedUnitPropertyIds, setRequestedUnitPropertyIds] = useState<
    string[]
  >([]);
  const [
    requestedPlmEventResponsePropertyIds,
    setRequestedPlmEventResponsePropertyIds,
  ] = useState<string[]>([]);
  const managedPropertyStoreRef = useRef<ManagedPropertyStore | null>(null);
  if (!managedPropertyStoreRef.current) {
    managedPropertyStoreRef.current = createManagedPropertyStore();
  }
  const managedPropertyStore = managedPropertyStoreRef.current;
  const managedUnitStoreRef = useRef<ManagedUnitStore | null>(null);
  if (!managedUnitStoreRef.current) {
    managedUnitStoreRef.current = createManagedUnitStore();
  }
  const managedUnitStore = managedUnitStoreRef.current;
  const lastHandledConnectionRefreshGeneration = useRef(
    connectionRefreshGeneration,
  );
  const lastHandledScopedUnitConnectionRefreshGeneration = useRef(
    connectionRefreshGeneration,
  );
  const lastHandledScopedPlmEventResponseConnectionRefreshGeneration = useRef(
    connectionRefreshGeneration,
  );

  const requestEntityData = useCallback(
    (entityKeys: ManagedEntityDataKey[]) => {
      setRequestedEntityData((prevRequestedEntityData) => {
        let changed = false;
        const nextRequestedEntityData = { ...prevRequestedEntityData };

        entityKeys.forEach((entityKey) => {
          if (!nextRequestedEntityData[entityKey]) {
            nextRequestedEntityData[entityKey] = true;
            changed = true;
          }
        });

        return changed ? nextRequestedEntityData : prevRequestedEntityData;
      });
    },
    [],
  );

  const requestUnitPropertyData = useCallback(
    (propertyId?: string | null | undefined) => {
      const normalizedPropertyId = normalizeManagedUnitStoreKey(propertyId);
      if (!normalizedPropertyId) {
        return;
      }

      setRequestedUnitPropertyIds((prevRequestedPropertyIds) =>
        prevRequestedPropertyIds.includes(normalizedPropertyId)
          ? prevRequestedPropertyIds
          : [...prevRequestedPropertyIds, normalizedPropertyId],
      );
    },
    [],
  );

  const requestPlmEventResponsePropertyData = useCallback(
    (propertyId?: string | null | undefined) => {
      const normalizedPropertyId =
        normalizeManagedPlmEventResponseStoreKey(propertyId);
      if (!normalizedPropertyId) {
        return;
      }

      setRequestedPlmEventResponsePropertyIds((prevRequestedPropertyIds) =>
        prevRequestedPropertyIds.includes(normalizedPropertyId)
          ? prevRequestedPropertyIds
          : [...prevRequestedPropertyIds, normalizedPropertyId],
      );
    },
    [],
  );

  // Use separate parallel queries for each entity type
  const {
    data: organizationsData,
    error: organizationsError,
    loading: organizationsLoading,
    refetch: refetchOrganizations,
  } = useManagedEntitiesOrganizationsQuery({
    fetchPolicy: 'network-only',
    skip: !requestedEntityData.organizations,
  });

  const {
    data: propertiesData,
    error: propertiesError,
    loading: propertiesLoading,
    refetch: refetchProperties,
  } = useManagedEntitiesPropertiesQuery({
    fetchPolicy: 'network-only',
    skip: !requestedEntityData.properties,
  });

  const {
    data: coresData,
    error: coresError,
    loading: coresLoading,
    refetch: refetchCores,
  } = useManagedEntitiesCoresQuery({
    fetchPolicy: 'network-only',
    skip: !requestedEntityData.cores,
  });

  const {
    data: hmmsData,
    error: hmmsError,
    loading: hmmsLoading,
    refetch: refetchHmms,
  } = useManagedEntitiesHmMsQuery({
    fetchPolicy: 'network-only',
    skip: !requestedEntityData.hmms,
  });

  const {
    data: bngsData,
    error: bngsError,
    loading: bngsLoading,
    refetch: refetchBngs,
  } = useManagedEntitiesBnGsQuery({
    fetchPolicy: 'network-only',
    skip: !requestedEntityData.bngs,
  });

  const {
    data: conductorsData,
    error: conductorsError,
    loading: conductorsLoading,
    refetch: refetchConductors,
  } = useManagedEntitiesConductorsQuery({
    fetchPolicy: 'network-only',
    skip: !requestedEntityData.conductors,
  });

  const {
    data: buildingsData,
    error: buildingsError,
    loading: buildingsLoading,
    refetch: refetchBuildings,
  } = useManagedEntitiesBuildingsQuery({
    fetchPolicy: 'network-only',
    skip: !requestedEntityData.buildings,
  });

  const {
    data: floorsData,
    error: floorsError,
    loading: floorsLoading,
    refetch: refetchFloors,
  } = useManagedEntitiesFloorsQuery({
    fetchPolicy: 'network-only',
    skip: !requestedEntityData.floors,
  });

  const {
    data: unitsData,
    error: unitsError,
    loading: unitsLoading,
    refetch: refetchUnits,
  } = useManagedEntitiesUnitsQuery({
    fetchPolicy: 'network-only',
    skip: !requestedEntityData.units,
  });

  const {
    data: unitsByPropertyIdsData,
    error: unitsByPropertyIdsError,
    loading: unitsByPropertyIdsLoading,
    refetch: refetchUnitsByPropertyIds,
  } = useManagedEntitiesUnitsByPropertyIdsQuery({
    fetchPolicy: 'network-only',
    variables: { propertyIds: requestedUnitPropertyIds },
    skip: requestedEntityData.units || requestedUnitPropertyIds.length === 0,
  });

  const {
    data: plmEventResponsesData,
    error: plmEventResponsesError,
    loading: plmEventResponsesLoading,
    refetch: refetchPlmEventResponses,
  } = useManagedEntitiesPlmEventResponsesQuery({
    fetchPolicy: 'network-only',
    skip: !canAccessPlm || !requestedEntityData.plmEventResponses,
  });

  const {
    data: plmEventResponsesByPropertyIdsData,
    error: plmEventResponsesByPropertyIdsError,
    loading: plmEventResponsesByPropertyIdsLoading,
    refetch: refetchPlmEventResponsesByPropertyIds,
  } = useManagedEntitiesPlmEventResponsesByPropertyIdsQuery({
    fetchPolicy: 'network-only',
    variables: { propertyIds: requestedPlmEventResponsePropertyIds },
    skip:
      !canAccessPlm ||
      requestedEntityData.plmEventResponses ||
      requestedPlmEventResponsePropertyIds.length === 0,
  });

  // Compute global loading and error states
  const loadingStates: IEntityManagerLoadingStates = useMemo(
    () => ({
      organizations: organizationsLoading,
      properties: propertiesLoading,
      cores: coresLoading,
      hmms: hmmsLoading,
      bngs: bngsLoading,
      conductors: conductorsLoading,
      buildings: buildingsLoading,
      floors: floorsLoading,
      units: unitsLoading,
      plmEventResponses: canAccessPlm
        ? plmEventResponsesLoading || plmEventResponsesByPropertyIdsLoading
        : false,
    }),
    [
      organizationsLoading,
      propertiesLoading,
      coresLoading,
      hmmsLoading,
      bngsLoading,
      conductorsLoading,
      buildingsLoading,
      floorsLoading,
      unitsLoading,
      canAccessPlm,
      plmEventResponsesLoading,
      plmEventResponsesByPropertyIdsLoading,
    ],
  );

  const errors: IEntityManagerErrors = useMemo(
    () => ({
      organizations: organizationsError || undefined,
      properties: propertiesError || undefined,
      cores: coresError || undefined,
      hmms: hmmsError || undefined,
      bngs: bngsError || undefined,
      conductors: conductorsError || undefined,
      buildings: buildingsError || undefined,
      floors: floorsError || undefined,
      units: unitsError || undefined,
      plmEventResponses: canAccessPlm
        ? plmEventResponsesError ||
          plmEventResponsesByPropertyIdsError ||
          undefined
        : undefined,
    }),
    [
      organizationsError,
      propertiesError,
      coresError,
      hmmsError,
      bngsError,
      conductorsError,
      buildingsError,
      floorsError,
      unitsError,
      canAccessPlm,
      plmEventResponsesError,
      plmEventResponsesByPropertyIdsError,
    ],
  );

  const loading = useMemo(
    () => Object.values(loadingStates).some((isLoading) => isLoading === true),
    [loadingStates],
  );

  const error = useMemo(
    () => Object.values(errors).find((err) => err !== undefined) || undefined,
    [errors],
  );

  const unitPropertyStatuses = useMemo(() => {
    if (requestedEntityData.units) {
      return {};
    }

    return requestedUnitPropertyIds.reduce(
      (res, propertyId) => {
        res[propertyId] = {
          loading: unitsByPropertyIdsLoading,
          error: unitsByPropertyIdsError || undefined,
        };
        return res;
      },
      {} as Record<string, ManagedUnitStoreStatus>,
    );
  }, [
    requestedEntityData.units,
    requestedUnitPropertyIds,
    unitsByPropertyIdsLoading,
    unitsByPropertyIdsError,
  ]);

  const scopedUnitSubscriptionRequested =
    !requestedEntityData.units && requestedUnitPropertyIds.length > 0;
  const plmEventResponseSubscriptionRequested =
    canAccessPlm &&
    (requestedEntityData.plmEventResponses ||
      requestedPlmEventResponsePropertyIds.length > 0);

  // Immediately clear managed entities when signing out to prevent subscription updates
  useEffect(() => {
    if (signingOut) {
      setManagedEntities(defaultValue);
      setRequestedEntityData(defaultEntityDemandState);
      setRequestedUnitPropertyIds([]);
      setRequestedPlmEventResponsePropertyIds([]);
    }
  }, [signingOut, setManagedEntities]);

  useEffect(() => {
    managedPropertyStore.replaceProperties(managedEntities.properties, {
      loading: managedEntities.loadingStates.properties,
      error: managedEntities.errors.properties || managedEntities.error,
    });
  }, [
    managedPropertyStore,
    managedEntities.properties,
    managedEntities.loadingStates.properties,
    managedEntities.errors.properties,
    managedEntities.error,
  ]);

  useEffect(() => {
    managedUnitStore.replaceUnits(
      managedEntities.units,
      {
        loading: managedEntities.loadingStates.units,
        error: managedEntities.errors.units || managedEntities.error,
      },
      unitPropertyStatuses,
    );
  }, [
    managedUnitStore,
    managedEntities.units,
    managedEntities.loadingStates.units,
    managedEntities.errors.units,
    managedEntities.error,
    unitPropertyStatuses,
  ]);

  useEffect(() => {
    if (
      signingOut ||
      connectionRefreshGeneration ===
        lastHandledConnectionRefreshGeneration.current
    ) {
      return;
    }

    lastHandledConnectionRefreshGeneration.current =
      connectionRefreshGeneration;

    const startedAt = Date.now();
    const refetches: Promise<unknown>[] = [];
    const addRequestedRefetch = (
      entityKey: ManagedEntityDataKey,
      refetch: () => Promise<unknown>,
    ) => {
      if (requestedEntityData[entityKey]) {
        refetches.push(refetch());
      }
    };

    addRequestedRefetch('organizations', refetchOrganizations);
    addRequestedRefetch('properties', refetchProperties);
    addRequestedRefetch('cores', refetchCores);
    addRequestedRefetch('hmms', refetchHmms);
    addRequestedRefetch('bngs', refetchBngs);
    addRequestedRefetch('conductors', refetchConductors);
    addRequestedRefetch('buildings', refetchBuildings);
    addRequestedRefetch('floors', refetchFloors);
    addRequestedRefetch('units', refetchUnits);

    if (canAccessPlm) {
      addRequestedRefetch('plmEventResponses', refetchPlmEventResponses);
    }

    if (refetches.length === 0) {
      return;
    }

    Log.silly(
      'Refreshing requested managed entity data after connection refresh.',
      {
        connectionRefreshGeneration,
        requestedEntityData,
        refetchCount: refetches.length,
      },
      LogCategory.DATA,
    );

    Promise.allSettled(refetches).then((results) => {
      Log.silly(
        'Finished requested managed entity refresh after connection refresh.',
        {
          connectionRefreshGeneration,
          durationMs: Date.now() - startedAt,
          rejectedCount: results.filter(
            (result) => result.status === 'rejected',
          ).length,
        },
        LogCategory.DATA,
      );
    });
  }, [
    signingOut,
    connectionRefreshGeneration,
    requestedEntityData,
    canAccessPlm,
    refetchOrganizations,
    refetchProperties,
    refetchCores,
    refetchHmms,
    refetchBngs,
    refetchConductors,
    refetchBuildings,
    refetchFloors,
    refetchUnits,
    refetchPlmEventResponses,
  ]);

  useEffect(() => {
    if (
      signingOut ||
      requestedEntityData.units ||
      requestedUnitPropertyIds.length === 0 ||
      connectionRefreshGeneration ===
        lastHandledScopedUnitConnectionRefreshGeneration.current
    ) {
      return;
    }

    lastHandledScopedUnitConnectionRefreshGeneration.current =
      connectionRefreshGeneration;

    const startedAt = Date.now();
    Log.silly(
      'Refreshing property-scoped managed unit data after connection refresh.',
      {
        connectionRefreshGeneration,
        requestedUnitPropertyIds,
      },
      LogCategory.DATA,
    );

    refetchUnitsByPropertyIds()
      .then(() => {
        Log.silly(
          'Finished property-scoped managed unit refresh after connection refresh.',
          {
            connectionRefreshGeneration,
            durationMs: Date.now() - startedAt,
          },
          LogCategory.DATA,
        );
      })
      .catch((err) => {
        Log.error(
          'Property-scoped managed unit refresh failed after connection refresh.',
          err,
          LogCategory.DATA,
        );
      });
  }, [
    signingOut,
    connectionRefreshGeneration,
    requestedEntityData.units,
    requestedUnitPropertyIds,
    refetchUnitsByPropertyIds,
  ]);

  useEffect(() => {
    if (
      signingOut ||
      !canAccessPlm ||
      requestedEntityData.plmEventResponses ||
      requestedPlmEventResponsePropertyIds.length === 0 ||
      connectionRefreshGeneration ===
        lastHandledScopedPlmEventResponseConnectionRefreshGeneration.current
    ) {
      return;
    }

    lastHandledScopedPlmEventResponseConnectionRefreshGeneration.current =
      connectionRefreshGeneration;

    const startedAt = Date.now();
    Log.silly(
      'Refreshing property-scoped managed PLM event response data after connection refresh.',
      {
        connectionRefreshGeneration,
        requestedPlmEventResponsePropertyIds,
      },
      LogCategory.DATA,
    );

    refetchPlmEventResponsesByPropertyIds()
      .then(() => {
        Log.silly(
          'Finished property-scoped managed PLM event response refresh after connection refresh.',
          {
            connectionRefreshGeneration,
            durationMs: Date.now() - startedAt,
          },
          LogCategory.DATA,
        );
      })
      .catch((err) => {
        Log.error(
          'Property-scoped managed PLM event response refresh failed after connection refresh.',
          err,
          LogCategory.DATA,
        );
      });
  }, [
    signingOut,
    canAccessPlm,
    connectionRefreshGeneration,
    requestedEntityData.plmEventResponses,
    requestedPlmEventResponsePropertyIds,
    refetchPlmEventResponsesByPropertyIds,
  ]);

  const updateSourceMap = useCallback((newManagedEntities: IEntityManager) => {
    newManagedEntities.sources = [
      ...Object.values(newManagedEntities.cores),
      ...Object.values(newManagedEntities.hmms),
      ...Object.values(newManagedEntities.bngs),
      ...Object.values(newManagedEntities.conductors),
    ].reduce((res, entity) => {
      res[entity._id] = entity;
      return res;
    }, {} as ManagedSourcesMap);
  }, []);

  const scopedUnitsData = unitsByPropertyIdsLoading
    ? undefined
    : unitsByPropertyIdsData?.unitsByPropertyId;
  const scopedPlmEventResponsesData = plmEventResponsesByPropertyIdsLoading
    ? undefined
    : plmEventResponsesByPropertyIdsData?.plmEventResponses;

  // Update entities as each query completes
  useEffect(() => {
    if (signingOut) {
      return;
    }

    setManagedEntities((prevManagedEntities) => {
      const newManagedEntities: IEntityManager = {
        ...prevManagedEntities,
        loading,
        loadingStates,
        error,
        errors,
        organizations: organizationsData?.organizations
          ? organizationsData.organizations.reduce((res, entity) => {
              res[entity._id] = entity;
              return res;
            }, {} as ManagedOrganizationsMap)
          : prevManagedEntities.organizations,
        properties: propertiesData?.properties
          ? propertiesData.properties.reduce((res, entity) => {
              res[entity._id] = entity;
              return res;
            }, {} as ManagedPropertiesMap)
          : prevManagedEntities.properties,
        cores: coresData?.cores
          ? coresData.cores.reduce((res, entity) => {
              res[entity._id] = entity;
              return res;
            }, {} as ManagedCoresMap)
          : prevManagedEntities.cores,
        hmms: hmmsData?.hMMs
          ? hmmsData.hMMs.reduce((res, entity) => {
              res[entity._id] = entity;
              return res;
            }, {} as ManagedHmmsMap)
          : prevManagedEntities.hmms,
        bngs: bngsData?.bNGs
          ? bngsData.bNGs.reduce((res, entity) => {
              res[entity._id] = entity;
              return res;
            }, {} as ManagedBngsMap)
          : prevManagedEntities.bngs,
        conductors: conductorsData?.conductors
          ? conductorsData.conductors.reduce((res, entity) => {
              res[entity._id] = entity;
              return res;
            }, {} as ManagedConductorsMap)
          : prevManagedEntities.conductors,
        buildings: buildingsData?.buildings
          ? buildingsData.buildings.reduce((res, entity) => {
              res[entity._id] = entity;
              return res;
            }, {} as ManagedBuildingsMap)
          : prevManagedEntities.buildings,
        floors: floorsData?.floors
          ? floorsData.floors.reduce((res, entity) => {
              res[entity._id] = entity;
              return res;
            }, {} as ManagedFloorsMap)
          : prevManagedEntities.floors,
        units: unitsData?.units
          ? createManagedUnitsMap(unitsData.units)
          : scopedUnitsData
            ? mergeManagedUnitsForProperties(
                prevManagedEntities.units,
                scopedUnitsData,
                requestedUnitPropertyIds,
              )
            : prevManagedEntities.units,
        plmEventResponses: canAccessPlm
          ? plmEventResponsesData?.plmEventResponses
            ? createManagedPlmEventResponsesMap(
                plmEventResponsesData.plmEventResponses,
              )
            : scopedPlmEventResponsesData
              ? mergeManagedPlmEventResponsesForProperties(
                  prevManagedEntities.plmEventResponses,
                  scopedPlmEventResponsesData,
                  requestedPlmEventResponsePropertyIds,
                )
              : prevManagedEntities.plmEventResponses
          : {},
      };

      updateSourceMap(newManagedEntities);
      return newManagedEntities;
    });
  }, [
    signingOut,
    loading,
    loadingStates,
    error,
    errors,
    organizationsData,
    propertiesData,
    coresData,
    hmmsData,
    bngsData,
    conductorsData,
    buildingsData,
    floorsData,
    unitsData,
    scopedUnitsData,
    requestedUnitPropertyIds,
    scopedPlmEventResponsesData,
    requestedPlmEventResponsePropertyIds,
    canAccessPlm,
    plmEventResponsesData,
    updateSourceMap,
  ]);

  const updateManagedEntitiesCollection = useCallback(
    <T extends UpdatedEntity>(
      updatedEntity: T,
      collectionKey: keyof IEntityManagerData,
      prevManagedEntities: IEntityManager,
    ) => {
      if (!updatedEntity) return prevManagedEntities;

      const entityType = singularize(collectionKey);

      const newEntityCollection = { ...prevManagedEntities[collectionKey] };
      const newManagedEntities = {
        ...prevManagedEntities,
        [collectionKey]: newEntityCollection,
      };

      const entityToUpdate = { ...updatedEntity };
      delete entityToUpdate.__typename;
      delete entityToUpdate.modelChangeOperation;

      switch (updatedEntity.modelChangeOperation?.toUpperCase()) {
        case 'INSERT':
          const existingEntity = newEntityCollection[entityToUpdate._id];
          if (!existingEntity) {
            newEntityCollection[updatedEntity._id] = entityToUpdate;
          } else {
            console.log(`Attempt to insert already existing ${entityType}!`);
            return prevManagedEntities;
          }
          break;
        case 'UPDATE':
          if (newEntityCollection[entityToUpdate._id]) {
            newEntityCollection[entityToUpdate._id] = Object.assign(
              {},
              newEntityCollection[entityToUpdate._id],
              entityToUpdate,
            );
          } else {
            console.log(`Attempt to update unknown (new?) ${entityType}!`);
            return prevManagedEntities;
          }
          break;
        case 'DELETE':
          if (newEntityCollection[entityToUpdate._id]) {
            delete newEntityCollection[entityToUpdate._id];
          } else {
            console.log(`Attempt to delete unknown ${entityType}!`);
            return prevManagedEntities;
          }
          break;
        default:
          Log.error(
            `Unhandled ${entityType} update operation: ${updatedEntity.modelChangeOperation}`,
            updatedEntity,
            LogCategory.DATA,
          );
      }

      if (['cores', 'hmms', 'bngs', 'conductors'].includes(collectionKey)) {
        updateSourceMap(newManagedEntities);
      }
      return newManagedEntities;
    },
    [updateSourceMap],
  );

  const processManagedEntityUpdate = useCallback(
    (
      updatedEntity: UpdatedEntity | null | undefined,
      collectionKey: keyof IEntityManagerData,
    ) => {
      // Don't process updates if signing out
      if (signingOut) {
        return;
      }

      if (updatedEntity) {
        setManagedEntities((prevManagedEntities) => {
          return updateManagedEntitiesCollection(
            updatedEntity,
            collectionKey,
            prevManagedEntities,
          );
        });
      } else {
        console.log(
          `Attempt to process empty managed ${singularize(collectionKey)} update.`,
        );
      }
    },
    [setManagedEntities, signingOut, updateManagedEntitiesCollection],
  );

  const managedOrganizationSubscriptionHandler = (
    options: OnDataOptions<ManagedOrganizationUpdatesSubscription>,
  ) => {
    processManagedEntityUpdate(
      options.data.data?.organizationEvents,
      'organizations',
    );
  };

  const managedPropertySubscriptionHandler = (
    options: OnDataOptions<ManagedPropertyUpdatesSubscription>,
  ) => {
    processManagedEntityUpdate(options.data.data?.propertyEvents, 'properties');
  };

  const managedCoreSubscriptionHandler = (
    options: OnDataOptions<ManagedCoreUpdatesSubscription>,
  ) => {
    processManagedEntityUpdate(options.data.data?.coreEvents, 'cores');
  };

  const managedHMMSubscriptionHandler = (
    options: OnDataOptions<ManagedHmmUpdatesSubscription>,
  ) => {
    processManagedEntityUpdate(options.data.data?.hMMEvents, 'hmms');
  };

  const managedBNGSubscriptionHandler = (
    options: OnDataOptions<ManagedBngUpdatesSubscription>,
  ) => {
    processManagedEntityUpdate(options.data.data?.bNGEvents, 'bngs');
  };

  const managedConductorSubscriptionHandler = (
    options: OnDataOptions<ManagedConductorUpdatesSubscription>,
  ) => {
    processManagedEntityUpdate(
      options.data.data?.conductorEvents,
      'conductors',
    );
  };

  const managedBuildingSubscriptionHandler = (
    options: OnDataOptions<ManagedBuildingUpdatesSubscription>,
  ) => {
    processManagedEntityUpdate(options.data.data?.buildingEvents, 'buildings');
  };

  const managedFloorSubscriptionHandler = (
    options: OnDataOptions<ManagedFloorUpdatesSubscription>,
  ) => {
    processManagedEntityUpdate(options.data.data?.floorEvents, 'floors');
  };

  const managedUnitSubscriptionHandler = (
    options: OnDataOptions<ManagedUnitUpdatesSubscription>,
  ) => {
    processManagedEntityUpdate(options.data.data?.unitEvents, 'units');
  };

  const managedUnitByPropertyIdsSubscriptionHandler = (
    options: OnDataOptions<ManagedUnitUpdatesByPropertyIdsSubscription>,
  ) => {
    processManagedEntityUpdate(
      options.data.data?.unitEventsByPropertyId,
      'units',
    );
  };

  const managedPlmEventResponseSubscriptionHandler = (
    options: OnDataOptions<ManagedPlmEventResponseUpdatesSubscription>,
  ) => {
    const updatedPlmEventResponse = options.data.data?.plmEventResponseEvents;
    if (
      updatedPlmEventResponse &&
      !requestedEntityData.plmEventResponses &&
      (!updatedPlmEventResponse.propertyId ||
        !requestedPlmEventResponsePropertyIds.includes(
          updatedPlmEventResponse.propertyId,
        ))
    ) {
      return;
    }

    processManagedEntityUpdate(updatedPlmEventResponse, 'plmEventResponses');
  };

  useManagedOrganizationUpdatesSubscription({
    fetchPolicy: 'no-cache',
    skip: !requestedEntityData.organizations,
    onData: managedOrganizationSubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error('Organization subscription error', error, LogCategory.DATA);
    },
  });

  useManagedPropertyUpdatesSubscription({
    fetchPolicy: 'no-cache',
    skip: !requestedEntityData.properties,
    onData: managedPropertySubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error('Property subscription error', error, LogCategory.DATA);
    },
  });

  useManagedCoreUpdatesSubscription({
    fetchPolicy: 'no-cache',
    skip: !requestedEntityData.cores,
    onData: managedCoreSubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error('Core subscription error', error, LogCategory.DATA);
    },
  });

  useManagedHmmUpdatesSubscription({
    fetchPolicy: 'no-cache',
    skip: !requestedEntityData.hmms,
    onData: managedHMMSubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error('HMM subscription error', error, LogCategory.DATA);
    },
  });

  useManagedBngUpdatesSubscription({
    fetchPolicy: 'no-cache',
    skip: !requestedEntityData.bngs,
    onData: managedBNGSubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error('BNG subscription error', error, LogCategory.DATA);
    },
  });

  useManagedConductorUpdatesSubscription({
    fetchPolicy: 'no-cache',
    skip: !requestedEntityData.conductors,
    onData: managedConductorSubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error('Conductor subscription error', error, LogCategory.DATA);
    },
  });

  useManagedBuildingUpdatesSubscription({
    fetchPolicy: 'no-cache',
    skip: !requestedEntityData.buildings,
    onData: managedBuildingSubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error('Building subscription error', error, LogCategory.DATA);
    },
  });

  useManagedFloorUpdatesSubscription({
    fetchPolicy: 'no-cache',
    skip: !requestedEntityData.floors,
    onData: managedFloorSubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error('Floor subscription error', error, LogCategory.DATA);
    },
  });

  useManagedUnitUpdatesSubscription({
    fetchPolicy: 'no-cache',
    skip: !requestedEntityData.units,
    onData: managedUnitSubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error('Unit subscription error', error, LogCategory.DATA);
    },
  });

  useManagedUnitUpdatesByPropertyIdsSubscription({
    fetchPolicy: 'no-cache',
    variables: { propertyIds: requestedUnitPropertyIds },
    skip: !scopedUnitSubscriptionRequested,
    onData: managedUnitByPropertyIdsSubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error(
        'Property-scoped unit subscription error',
        error,
        LogCategory.DATA,
      );
    },
  });

  useManagedPlmEventResponseUpdatesSubscription({
    fetchPolicy: 'no-cache',
    skip: !plmEventResponseSubscriptionRequested,
    onData: managedPlmEventResponseSubscriptionHandler,
    onError: (error) => {
      if (signingOut) {
        // Silently ignore errors during sign out to prevent server log noise
        return;
      }
      Log.error('PlmEventResponse subscription error', error, LogCategory.DATA);
    },
  });

  const value: IEntityManager = useMemo(
    () => ({
      ...managedEntities,
      requestedEntityData,
      requestEntityData,
    }),
    [managedEntities, requestedEntityData, requestEntityData],
  );
  const demandValue: ManagedEntityDemandContextValue = useMemo(
    () => ({
      requestedEntityData,
      requestedUnitPropertyIds,
      requestedPlmEventResponsePropertyIds,
      requestEntityData,
      requestUnitPropertyData,
      requestPlmEventResponsePropertyData,
    }),
    [
      requestedEntityData,
      requestedUnitPropertyIds,
      requestedPlmEventResponsePropertyIds,
      requestEntityData,
      requestUnitPropertyData,
      requestPlmEventResponsePropertyData,
    ],
  );

  Log.silly('rendering entity manager', null, LogCategory.RENDERING);

  return (
    <ManagedEntityDemandContext.Provider value={demandValue}>
      <ManagedPropertyStoreContext.Provider value={managedPropertyStore}>
        <ManagedUnitStoreContext.Provider value={managedUnitStore}>
          <ManagedEntitiesContext.Provider value={value}>
            {children}
          </ManagedEntitiesContext.Provider>
        </ManagedUnitStoreContext.Provider>
      </ManagedPropertyStoreContext.Provider>
    </ManagedEntityDemandContext.Provider>
  );
};

export function useEntityManager(): IEntityManager {
  const context = useContext(ManagedEntitiesContext);
  if (context === undefined) {
    throw new Error(
      'useEntityManager must be used within an ManagedEntitiesProvider',
    );
  }
  return context;
}

function useManagedEntityDemand(): ManagedEntityDemandContextValue {
  const context = useContext(ManagedEntityDemandContext);
  if (context === undefined) {
    throw new Error(
      'useManagedEntityDemand must be used within an ManagedEntitiesProvider',
    );
  }
  return context;
}

function useManagedUnitStore(): ManagedUnitStore {
  const context = useContext(ManagedUnitStoreContext);
  if (context === undefined) {
    throw new Error(
      'useManagedUnitStore must be used within an ManagedEntitiesProvider',
    );
  }
  return context;
}

function useManagedPropertyStore(): ManagedPropertyStore {
  const context = useContext(ManagedPropertyStoreContext);
  if (context === undefined) {
    throw new Error(
      'useManagedPropertyStore must be used within an ManagedEntitiesProvider',
    );
  }
  return context;
}

const EMPTY_ENTITY_REQUIREMENTS: ManagedEntityDataKey[] = [];
const UNITS_ENTITY_REQUIREMENTS: ManagedEntityDataKey[] = ['units'];
const SOURCES_ENTITY_REQUIREMENTS: ManagedEntityDataKey[] = [
  'cores',
  'hmms',
  'bngs',
  'conductors',
];
const PLM_ENTITY_REQUIREMENTS: ManagedEntityDataKey[] = ['plmEventResponses'];

function useRequiredEntityData(entityKeys: ManagedEntityDataKey[]): boolean {
  const { requestedEntityData, requestEntityData } = useManagedEntityDemand();
  const entityDataRequested = entityKeys.every(
    (entityKey) => requestedEntityData[entityKey],
  );

  useEffect(() => {
    if (entityKeys.length > 0 && !entityDataRequested) {
      requestEntityData(entityKeys);
    }
  }, [entityDataRequested, entityKeys, requestEntityData]);

  return entityDataRequested;
}

function useRequiredUnitsByPropertyData(
  propertyId: string | null | undefined,
): boolean {
  const {
    requestedEntityData,
    requestedUnitPropertyIds,
    requestUnitPropertyData,
  } = useManagedEntityDemand();
  const normalizedPropertyId = normalizeManagedUnitStoreKey(propertyId);
  const unitDataRequested =
    requestedEntityData.units ||
    (!!normalizedPropertyId &&
      requestedUnitPropertyIds.includes(normalizedPropertyId));

  useEffect(() => {
    if (normalizedPropertyId && !unitDataRequested) {
      requestUnitPropertyData(normalizedPropertyId);
    }
  }, [normalizedPropertyId, unitDataRequested, requestUnitPropertyData]);

  return unitDataRequested;
}

function useRequiredPlmEventResponsesByPropertyData(
  propertyId: string | null | undefined,
): boolean {
  const {
    requestedEntityData,
    requestedPlmEventResponsePropertyIds,
    requestPlmEventResponsePropertyData,
  } = useManagedEntityDemand();
  const normalizedPropertyId =
    normalizeManagedPlmEventResponseStoreKey(propertyId);
  const plmEventResponseDataRequested =
    requestedEntityData.plmEventResponses ||
    (!!normalizedPropertyId &&
      requestedPlmEventResponsePropertyIds.includes(normalizedPropertyId));

  useEffect(() => {
    if (normalizedPropertyId && !plmEventResponseDataRequested) {
      requestPlmEventResponsePropertyData(normalizedPropertyId);
    }
  }, [
    normalizedPropertyId,
    plmEventResponseDataRequested,
    requestPlmEventResponsePropertyData,
  ]);

  return plmEventResponseDataRequested;
}

function useManagedUnitSnapshot(id: string | null | undefined): IHookUnitById {
  const managedUnitStore = useManagedUnitStore();
  const normalizedId = normalizeManagedUnitStoreKey(id);
  const subscribe = useCallback(
    (listener: () => void) =>
      managedUnitStore.subscribeUnit(normalizedId, listener),
    [managedUnitStore, normalizedId],
  );
  const getSnapshot = useCallback(
    () => managedUnitStore.getUnitSnapshot(normalizedId),
    [managedUnitStore, normalizedId],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function useManagedUnitsByPropertySnapshot(
  propertyId: string,
): IHookUnitsByPropertyId {
  const managedUnitStore = useManagedUnitStore();
  const normalizedPropertyId = normalizeManagedUnitStoreKey(propertyId);
  const subscribe = useCallback(
    (listener: () => void) =>
      managedUnitStore.subscribeUnitsByProperty(normalizedPropertyId, listener),
    [managedUnitStore, normalizedPropertyId],
  );
  const getSnapshot = useCallback(
    () => managedUnitStore.getUnitsByPropertySnapshot(normalizedPropertyId),
    [managedUnitStore, normalizedPropertyId],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function useManagedPropertiesSnapshot(): IHookProperties {
  const managedPropertyStore = useManagedPropertyStore();
  const subscribe = useCallback(
    (listener: () => void) =>
      managedPropertyStore.subscribeProperties(listener),
    [managedPropertyStore],
  );
  const getSnapshot = useCallback(
    () => managedPropertyStore.getPropertiesSnapshot(),
    [managedPropertyStore],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function useManagedPropertySnapshot(
  id: string | null | undefined,
): IHookPropertyById {
  const managedPropertyStore = useManagedPropertyStore();
  const normalizedId = normalizeManagedPropertyStoreKey(id);
  const subscribe = useCallback(
    (listener: () => void) =>
      managedPropertyStore.subscribeProperty(normalizedId, listener),
    [managedPropertyStore, normalizedId],
  );
  const getSnapshot = useCallback(
    () => managedPropertyStore.getPropertySnapshot(normalizedId),
    [managedPropertyStore, normalizedId],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export interface IHookOrganizations {
  organizations: ManagedOrganization[];
  loading: boolean;
  error?: ApolloError;
}

export function useOrganizations(): IHookOrganizations {
  const { organizations, loadingStates, errors, error } = useEntityManager();

  return useMemo(() => {
    const entityLoading = loadingStates.organizations;
    const entityError = errors.organizations || error;
    return entityLoading
      ? { loading: true, organizations: [] }
      : entityError
        ? { error: entityError, organizations: [], loading: false }
        : {
            organizations: Object.values(organizations),
            loading: false,
          };
  }, [organizations, loadingStates.organizations, errors.organizations, error]);
}

export function useProperties(): IHookProperties {
  const propertiesSnapshot = useManagedPropertiesSnapshot();

  return useMemo(() => {
    const entityLoading = propertiesSnapshot.loading;
    const entityError = propertiesSnapshot.error;
    return entityLoading
      ? { loading: true, properties: [] }
      : entityError
        ? { error: entityError, properties: [], loading: false }
        : {
            properties: propertiesSnapshot.properties,
            loading: false,
          };
  }, [propertiesSnapshot]);
}

export function usePropertyById(
  id: string | null | undefined,
): IHookPropertyById {
  const propertySnapshot = useManagedPropertySnapshot(id);

  return useMemo(() => {
    const entityLoading = propertySnapshot.loading;
    const entityError = propertySnapshot.error;
    return {
      property:
        entityError || entityLoading || !id ? null : propertySnapshot.property,
      error: entityError,
      loading: entityLoading,
    };
  }, [id, propertySnapshot]);
}

export interface IHookPlmEventResponses {
  plmEventResponses: ManagedPlmEventResponse[];
  loading: boolean;
  error?: ApolloError;
}

export function usePlmEventResponses(): IHookPlmEventResponses {
  const entityDataRequested = useRequiredEntityData(PLM_ENTITY_REQUIREMENTS);
  const { plmEventResponses, loadingStates, errors, error } =
    useEntityManager();

  return useMemo(() => {
    const entityLoading =
      !entityDataRequested || loadingStates.plmEventResponses;
    const entityError = errors.plmEventResponses || error;
    return entityLoading
      ? { loading: true, plmEventResponses: [] }
      : entityError
        ? { error: entityError, plmEventResponses: [], loading: false }
        : {
            plmEventResponses: Object.values(plmEventResponses),
            loading: false,
          };
  }, [
    plmEventResponses,
    entityDataRequested,
    loadingStates.plmEventResponses,
    errors.plmEventResponses,
    error,
  ]);
}

export interface IHookSources {
  sources: ManagedSource[];
  loading: boolean;
  error?: ApolloError;
}

export function useSources(): IHookSources {
  const entityDataRequested = useRequiredEntityData(
    SOURCES_ENTITY_REQUIREMENTS,
  );
  const { sources, loadingStates, errors, error } = useEntityManager();

  return useMemo(() => {
    // Sources depend on all source discriminator collections.
    const entityLoading =
      !entityDataRequested ||
      loadingStates.cores ||
      loadingStates.hmms ||
      loadingStates.bngs ||
      loadingStates.conductors;
    const entityError =
      errors.cores || errors.hmms || errors.bngs || errors.conductors || error;
    return entityLoading
      ? { loading: true, sources: [] }
      : entityError
        ? { error: entityError, sources: [], loading: false }
        : {
            sources: Object.values(sources),
            loading: false,
          };
  }, [
    sources,
    entityDataRequested,
    loadingStates.cores,
    loadingStates.hmms,
    loadingStates.bngs,
    loadingStates.conductors,
    errors.cores,
    errors.hmms,
    errors.bngs,
    errors.conductors,
    error,
  ]);
}

export interface IHookEntityMap {
  organizations: Record<string, ManagedOrganization>;
  properties: Record<string, ManagedProperty>;
  units: Record<string, ManagedUnit>;
  buildings: Record<string, ManagedBuilding>;
  floors: Record<string, ManagedFloor>;
}

export interface IHookBulkUpdateEntities {
  organizations: IHookEntityMap['organizations'][string][];
  properties: IHookEntityMap['properties'][string][];
  units: IHookEntityMap['units'][string][];
  buildings: IHookEntityMap['buildings'][string][];
  floors: IHookEntityMap['floors'][string][];
  devices: EntityManagerThermostat[];
  loading: boolean;
  error?: ApolloError;
}

export function useBulkUpdateEntities(
  selectedPropertyId?: string | null | undefined,
): IHookBulkUpdateEntities {
  const shouldLoadBulkEntities = Boolean(selectedPropertyId);
  const scopedUnitDataRequested =
    useRequiredUnitsByPropertyData(selectedPropertyId);
  const propertyUnitsSnapshot = useManagedUnitsByPropertySnapshot(
    selectedPropertyId ?? '',
  );
  const entities = useEntityManager();
  const propertyIdVariable = selectedPropertyId ?? '';

  const {
    data: rawBuildingsData,
    loading: buildingsLoading,
    error: buildingsError,
  } = useManagedEntitiesBuildingsByPropertyIdQuery({
    fetchPolicy: 'network-only',
    variables: { propertyId: propertyIdVariable },
    skip: !shouldLoadBulkEntities,
  });

  const {
    data: rawFloorsData,
    loading: floorsLoading,
    error: floorsError,
  } = useManagedEntitiesFloorsByPropertyIdQuery({
    fetchPolicy: 'network-only',
    variables: { propertyId: propertyIdVariable },
    skip: !shouldLoadBulkEntities,
  });

  const {
    data: rawThermostatData,
    loading: deviceLoading,
    error: devicesError,
  } = useManagedEntitiesThermostatsByPropertyIdQuery({
    fetchPolicy: 'network-only',
    variables: { propertyId: propertyIdVariable },
    skip: !shouldLoadBulkEntities,
  });

  return useMemo(() => {
    const { properties, loadingStates, errors, error } = entities;

    if (!shouldLoadBulkEntities) {
      const entityError = errors.properties || error;
      return entityError || loadingStates.properties
        ? {
            organizations: [],
            properties: [],
            units: [],
            buildings: [],
            floors: [],
            devices: [],
            loading: loadingStates.properties,
            error: entityError,
          }
        : {
            organizations: [],
            properties: Object.values(properties),
            units: [],
            buildings: [],
            floors: [],
            devices: [],
            loading: false,
          };
    }

    const thermostats = rawThermostatData?.thermostats ?? [];
    const buildings = (rawBuildingsData?.buildings ?? []).reduce(
      (result, building) => {
        result[building._id] = building;
        return result;
      },
      {} as IHookEntityMap['buildings'],
    );
    const floors = (rawFloorsData?.floors ?? []).reduce(
      (result, floor) => {
        result[floor._id] = floor;
        return result;
      },
      {} as IHookEntityMap['floors'],
    );
    const units = propertyUnitsSnapshot.units.reduce(
      (result, unit) => {
        result[unit._id] = unit;
        return result;
      },
      {} as IHookEntityMap['units'],
    );

    // Check if any required entity types are still loading
    const requiredLoading =
      loadingStates.properties ||
      !scopedUnitDataRequested ||
      propertyUnitsSnapshot.loading ||
      buildingsLoading ||
      floorsLoading ||
      deviceLoading;
    const requiredError =
      errors.properties ||
      propertyUnitsSnapshot.error ||
      buildingsError ||
      floorsError ||
      error ||
      devicesError;

    if (requiredLoading || requiredError) {
      return {
        organizations: [],
        properties: [],
        units: [],
        buildings: [],
        floors: [],
        devices: [],
        loading: requiredLoading,
        error: requiredError,
      };
    }

    const filteredEntities: IHookEntityMap = thermostats.reduce(
      (result, tStat) => {
        if (tStat) {
          // TODO: Peter: Add filtering for organization once organizationId is added to property model.
          const prop = properties[tStat.propertyId];
          if (prop) {
            result.properties[tStat.propertyId] = prop;
            const unit = units[tStat.unitId];
            if (unit) {
              result.units[unit._id] = unit;
              const building = buildings[unit.buildingId];
              if (building) {
                result.buildings[building._id] = building;
              }
              if (unit.floorId) {
                const floor = floors[unit.floorId];
                if (floor) {
                  result.floors[floor._id] = floor;
                }
              }
            }
          }
        }
        return result;
      },
      {
        organizations: {},
        properties: {},
        units: {},
        buildings: {},
        floors: {},
      } as IHookEntityMap,
    );

    return {
      organizations: Object.values(filteredEntities.organizations),
      properties: Object.values(filteredEntities.properties),
      units: Object.values(filteredEntities.units),
      buildings: Object.values(filteredEntities.buildings),
      floors: Object.values(filteredEntities.floors),
      devices: thermostats,
      loading: requiredLoading,
      error: requiredError,
    } as IHookBulkUpdateEntities;
  }, [
    entities,
    shouldLoadBulkEntities,
    scopedUnitDataRequested,
    propertyUnitsSnapshot,
    rawBuildingsData,
    rawFloorsData,
    rawThermostatData,
    buildingsLoading,
    floorsLoading,
    deviceLoading,
    buildingsError,
    floorsError,
    devicesError,
  ]);
}

export function useUnitsByPropertyId(
  propertyId: string,
): IHookUnitsByPropertyId {
  const entityDataRequested = useRequiredUnitsByPropertyData(propertyId);
  const unitSnapshot = useManagedUnitsByPropertySnapshot(propertyId);

  return useMemo(() => {
    const entityLoading = !entityDataRequested || unitSnapshot.loading;
    const entityError = unitSnapshot.error;
    return {
      units: entityError || entityLoading ? [] : unitSnapshot.units,
      error: entityError,
      loading: entityLoading,
    };
  }, [unitSnapshot, entityDataRequested]);
}

export interface IHookScheduledPlmResponsesForProperty {
  scheduledResponses: ManagedPlmEventResponse[];
  currentResponses: ManagedPlmEventResponse[];
  loading: boolean;
  error?: ApolloError;
}

export function useUnitById(
  id: string | null | undefined,
  propertyId?: string | null | undefined,
): IHookUnitById {
  const hasPropertyScope = Boolean(propertyId);
  const scopedEntityDataRequested = useRequiredUnitsByPropertyData(
    id && hasPropertyScope ? propertyId : undefined,
  );
  const broadEntityDataRequested = useRequiredEntityData(
    id && !hasPropertyScope
      ? UNITS_ENTITY_REQUIREMENTS
      : EMPTY_ENTITY_REQUIREMENTS,
  );
  const unitSnapshot = useManagedUnitSnapshot(id);

  return useMemo(() => {
    const entityDataRequested = hasPropertyScope
      ? scopedEntityDataRequested
      : broadEntityDataRequested;
    const entityLoading =
      (id ? !entityDataRequested : false) || unitSnapshot.loading;
    const entityError = unitSnapshot.error;
    return {
      unit: entityError || entityLoading || !id ? null : unitSnapshot.unit,
      error: entityError,
      loading: entityLoading,
    };
  }, [
    id,
    hasPropertyScope,
    scopedEntityDataRequested,
    broadEntityDataRequested,
    unitSnapshot,
  ]);
}

export interface IHookScheduledPlmResponsesForProperty {
  scheduledResponses: ManagedPlmEventResponse[];
  currentResponses: ManagedPlmEventResponse[];
  loading: boolean;
  error?: ApolloError;
}

export function useScheduledPlmResponsesForProperty(
  propertyId: string,
): IHookScheduledPlmResponsesForProperty {
  const entityDataRequested =
    useRequiredPlmEventResponsesByPropertyData(propertyId);
  const { plmEventResponses, loadingStates, errors, error } =
    useEntityManager();

  return useMemo(() => {
    const entityLoading =
      (propertyId ? !entityDataRequested : false) ||
      loadingStates.plmEventResponses;
    const entityError = errors.plmEventResponses || error;
    if (entityError || entityLoading) {
      return {
        scheduledResponses: [],
        currentResponses: [],
        loading: entityLoading,
        error: entityError,
      };
    }

    const currentTimestamp = Date.now();
    const propertyResponses = Object.values(plmEventResponses).filter(
      (response) => response.propertyId === propertyId,
    );

    const currentResponses = propertyResponses.filter(
      (response) =>
        response.responseStatus === 'active' &&
        response.actualStart &&
        response.actualStart <= currentTimestamp &&
        (!response.actualEnd || response.actualEnd > currentTimestamp),
    );

    const scheduledResponses = propertyResponses.filter(
      (response) =>
        response.responseStatus === 'scheduled' &&
        response.scheduledStart > currentTimestamp,
    );

    return {
      scheduledResponses,
      currentResponses,
      loading: false,
    };
  }, [
    propertyId,
    plmEventResponses,
    entityDataRequested,
    loadingStates.plmEventResponses,
    errors.plmEventResponses,
    error,
  ]);
}
